/**
 * Stock by depot — grouped the way the user chose.
 *
 * The page answers "where is the stock", and "where" is not one hierarchy: a
 * logistics person wants it by point of sale then depot, an accountant by company then
 * depot, somebody walking a region by location. So the levels are the user's: which of
 * company, location, point of sale and depot to use, and in what order, kept per user.
 * The default is company, location, point of sale, depot.
 *
 * A point of sale is a place, and several companies may keep a depot in the same one.
 * When the page groups by place, those depots are one line: their totals added, the
 * companies that share the place named beside it. Choosing a line opens what its depots
 * hold — the articles, and how many of each across all of them.
 *
 * Built from the stock structure's totals rather than from the stock lines, so a place
 * with no stock at all still has its line, at zero. A place missing from this page would
 * be a place nobody knew to restock.
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ApiClient } from "@hardauto/core-api";
import { useDebounced, useResource } from "@hardauto/core-data";
import { quantity } from "@hardauto/core-i18n";
import {
  Dialog,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  MultiSelect,
  Page,
  Toolbar,
  DataTable,
  type Column,
  TreeTable,
  type TreeColumn,
  type TreeRow,
} from "@hardauto/core-ui";

import { ReferentialApi, type StockGroupLevel, type StockLevel, type StructureNode } from "../api";
import { NAMESPACE } from "../i18n";
import { GroupingPicker } from "./GroupingPicker";
import { useGrouping, useLevelName } from "./grouping";
import "./referential.css";

/** The most lines the dialog asks for; past it the search narrows. */
const DIALOG_LIMIT = 200;

/** This page's own saving slot, and what it can group by. */
const LAYOUT_KEY = "stock_by_depot";
const ALLOWED: readonly StockGroupLevel[] = ["company", "city", "point", "site"];
const DEFAULT_LEVELS: readonly StockGroupLevel[] = ["company", "city", "point", "site"];

/** The key of what has no point of sale, no location or no company, grouped together. */
const NONE = "__none__";

/** One depot, with everything the levels above it are made of. */
interface Depot {
  code: string;
  name: string;
  companyCode: string;
  companyName: string;
  pointKey: string;
  pointName: string;
  city: string;
  lines: number;
  real: number;
  reserved: number;
  available: number;
}

/** Company → point of sale → depot, flattened to one entry per depot. */
function depotsOf(roots: readonly StructureNode[]): Depot[] {
  const depots: Depot[] = [];
  for (const company of roots) {
    for (const point of company.children) {
      for (const depot of point.children) {
        depots.push({
          code: depot.code,
          name: depot.name,
          companyCode: company.code,
          companyName: company.name || company.code,
          pointKey: point.code || NONE,
          pointName: point.name,
          // A depot's location is its place's city, or its own when it has no place.
          city: point.city || depot.city,
          lines: depot.lines,
          real: Number(depot.real),
          reserved: Number(depot.reserved),
          available: Number(depot.available),
        });
      }
    }
  }
  return depots;
}

/** A row of the page: a company, a location, a point of sale or a depot, whichever the user nested. */
interface StockNode {
  level: StockGroupLevel;
  name: string;
  code?: string;
  companies: string[];
  depots: number;
  lines: number;
  real: number;
  reserved: number;
  available: number;
  /** Every depot under the row: what choosing it opens. */
  depotCodes: string[];
  /** Where it is, for the dialog's subtitle. */
  place: string;
}

interface Words {
  noLocation: string;
  noPoint: string;
  noCompany: string;
  locale: string;
}

function keyOf(level: StockGroupLevel, depot: Depot): string {
  switch (level) {
    case "company":
      return depot.companyCode || NONE;
    case "city":
      return depot.city || NONE;
    case "point":
      return depot.pointKey;
    default:
      return depot.code;
  }
}

function nameOf(level: StockGroupLevel, depot: Depot, words: Words): string {
  switch (level) {
    case "company":
      return depot.companyCode ? depot.companyName : words.noCompany;
    case "city":
      return depot.city || words.noLocation;
    case "point":
      return depot.pointKey === NONE ? words.noPoint : depot.pointName || depot.pointKey;
    default:
      return depot.name;
  }
}

/** The depots nested by the levels the user chose, each row carrying the totals of what is under it. */
function stockRows(
  depots: readonly Depot[],
  levels: readonly StockGroupLevel[],
  words: Words,
  parent = "",
): TreeRow<StockNode>[] {
  const [level, ...rest] = levels;
  if (!level) return [];
  const groups = new Map<string, Depot[]>();
  for (const depot of depots) {
    const key = keyOf(level, depot);
    groups.set(key, [...(groups.get(key) ?? []), depot]);
  }
  return [...groups.entries()]
    .map(([key, members]) => ({ key, members, name: nameOf(level, members[0]!, words) }))
    .sort(
      (a, b) =>
        Number(a.key === NONE) - Number(b.key === NONE) ||
        a.name.localeCompare(b.name, words.locale),
    )
    .map(({ key, members, name }) => {
      const rowKey = `${parent}/${level}:${key}`;
      const children = stockRows(members, rest, words, rowKey);
      return {
        key: rowKey,
        node: {
          level,
          name,
          ...(level === "site" ? { code: members[0]!.code } : {}),
          companies: [...new Set(members.map((depot) => depot.companyName).filter(Boolean))],
          depots: members.length,
          lines: members.reduce((total, depot) => total + depot.lines, 0),
          real: members.reduce((total, depot) => total + depot.real, 0),
          reserved: members.reduce((total, depot) => total + depot.reserved, 0),
          available: members.reduce((total, depot) => total + depot.available, 0),
          depotCodes: members.map((depot) => depot.code),
          place: [...new Set(members.map((depot) => depot.city).filter(Boolean))].join(", "),
        },
        ...(children.length ? { children } : {}),
      };
    });
}

export function StockByDepotScreen({ client }: { client: ApiClient }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const api = useMemo(() => new ReferentialApi(client), [client]);
  const levelName = useLevelName();
  const grouping = useGrouping(api, LAYOUT_KEY, DEFAULT_LEVELS, ALLOWED);

  const [term, setTerm] = useState("");
  const [citiesChosen, setCitiesChosen] = useState<string[]>([]);
  const [companiesChosen, setCompaniesChosen] = useState<string[]>([]);
  const [opened, setOpened] = useState<StockNode | null>(null);

  const fetcher = useCallback(() => api.stockStructure("company", false), [api]);
  const tree = useResource(fetcher);

  // The company filter narrows the depots *before* they are grouped, so a point
  // of sale shared by two companies counts only the chosen one's stock.
  const all = useMemo(() => {
    const roots = (tree.data?.items ?? []).filter(
      (company) => companiesChosen.length === 0 || companiesChosen.includes(company.code),
    );
    return depotsOf(roots);
  }, [tree.data, companiesChosen]);

  const companyOptions = useMemo(
    () =>
      (tree.data?.items ?? []).map((company) => ({
        value: company.code,
        label: company.name || company.code,
      })),
    [tree.data],
  );
  const cities = useMemo(
    () =>
      [
        ...new Set(
          depotsOf(tree.data?.items ?? [])
            .map((depot) => depot.city)
            .filter(Boolean),
        ),
      ].sort((a, b) => a.localeCompare(b, i18n.language)),
    [tree.data, i18n.language],
  );

  const needle = term.trim().toLocaleLowerCase(i18n.language);
  const shown = all.filter(
    (depot) =>
      (citiesChosen.length === 0 || citiesChosen.includes(depot.city)) &&
      (!needle ||
        [depot.name, depot.code, depot.pointName, depot.city, depot.companyName].some((text) =>
          text.toLocaleLowerCase(i18n.language).includes(needle),
        )),
  );

  const qty = (value: number) => quantity(value.toFixed(3), i18n.language);

  const words: Words = {
    noLocation: t("stockByDepot.noLocation"),
    noPoint: t("stockByDepot.noPoint"),
    noCompany: t("stockByDepot.noCompany"),
    locale: i18n.language,
  };
  const rows = stockRows(shown, grouping.levels, words);
  const header = grouping.levels.map(levelName).join(" / ");

  const columns: TreeColumn<StockNode>[] = [
    {
      key: "name",
      header,
      render: (node) => (
        <button
          type="button"
          className="hardauto-stock-depot-link"
          title={t("stockByDepot.open")}
          onClick={() => setOpened(node)}
        >
          {node.name}
        </button>
      ),
    },
    {
      key: "companies",
      header: t("stockByDepot.column.companies"),
      render: (node) => node.companies.join(", ") || "—",
    },
    {
      key: "depots",
      header: t("stockByDepot.column.depots"),
      align: "end",
      render: (node) =>
        node.level === "site" ? "" : <span className="hardauto-num">{node.depots}</span>,
    },
    {
      key: "lines",
      header: t("stockByDepot.column.lines"),
      align: "end",
      render: (node) => <span className="hardauto-num">{node.lines}</span>,
    },
    {
      key: "available",
      header: t("stock.column.available"),
      align: "end",
      render: (node) => (
        <span className="hardauto-num hardauto-num--strong">{qty(node.available)}</span>
      ),
    },
    {
      key: "reserved",
      header: t("stock.column.reserved"),
      align: "end",
      render: (node) => <span className="hardauto-num">{qty(node.reserved)}</span>,
    },
    {
      key: "real",
      header: t("stock.column.real"),
      align: "end",
      render: (node) => <span className="hardauto-num">{qty(node.real)}</span>,
    },
  ];

  return (
    <Page title={t("stockByDepot.title")} description={t("stockByDepot.intro")}>
      <Toolbar>
        <div className="hardauto-search">
          <Input
            type="search"
            aria-label={t("stockByDepot.searchLabel")}
            placeholder={t("stockByDepot.searchPlaceholder")}
            value={term}
            onChange={(event) => setTerm(event.target.value)}
          />
        </div>
        <MultiSelect
          label={t("stock.cityLabel")}
          allLabel={t("stock.allCities")}
          options={cities.map((city) => ({ value: city, label: city }))}
          selected={citiesChosen}
          onChange={setCitiesChosen}
          countLabel={(count) => t("stock.selectedCount", { count })}
          clearLabel={t("stock.clearFilter")}
          searchLabel={t("stock.searchOptions")}
        />
        <MultiSelect
          label={t("stock.companyLabel")}
          allLabel={t("stock.allCompanies")}
          options={companyOptions}
          selected={companiesChosen}
          onChange={setCompaniesChosen}
          countLabel={(count) => t("stock.selectedCount", { count })}
          clearLabel={t("stock.clearFilter")}
          searchLabel={t("stock.searchOptions")}
        />
      </Toolbar>
      <GroupingPicker grouping={grouping} allowed={ALLOWED} />

      {tree.failed ? (
        <ErrorState title={t("error.title")} onRetry={tree.reload} retryLabel={t("error.retry")} />
      ) : (tree.loading && !tree.data) || !grouping.ready ? (
        <LoadingState label={t("stock.loading")} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={t("stockByDepot.empty.title")}
          description={t("stockByDepot.empty.body")}
        />
      ) : (
        <TreeTable<StockNode>
          // A new arrangement starts closed: the open lines belonged to the old one.
          key={grouping.levels.join(">")}
          caption={t("stockByDepot.caption")}
          columns={columns}
          rows={rows}
          toggleLabel={(node, open) =>
            `${open ? t("stockByDepot.collapse") : t("stockByDepot.expand")} ${node.name}`
          }
        />
      )}

      {opened ? <LineStockDialog api={api} node={opened} onClose={() => setOpened(null)} /> : null}
    </Page>
  );
}

/** One article across the depots of the point of sale. */
interface ArticleTotal {
  reference: string;
  designation: string;
  depots: number;
  available: number;
  reserved: number;
  real: number;
}

/** The stock lines of several depots, one line per article, depots added together. */
function totalsOf(levels: readonly StockLevel[]): ArticleTotal[] {
  const byArticle = new Map<string, ArticleTotal>();
  for (const level of levels) {
    const found = byArticle.get(level.article_reference) ?? {
      reference: level.article_reference,
      designation: level.article_designation,
      depots: 0,
      available: 0,
      reserved: 0,
      real: 0,
    };
    found.depots += 1;
    found.available += Number(level.available);
    found.reserved += Number(level.reserved);
    found.real += Number(level.real);
    byArticle.set(found.reference, found);
  }
  return [...byArticle.values()].sort((a, b) => a.reference.localeCompare(b.reference));
}

/** What one line holds, whatever its level: its articles and how many of each, across its depots. */
function LineStockDialog({
  api,
  node,
  onClose,
}: {
  api: ReferentialApi;
  node: StockNode;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const [term, setTerm] = useState("");
  const search = useDebounced(term, 250);

  const depotCodes = node.depotCodes;
  const name = node.name;
  const fetcher = useCallback(
    () =>
      api.listStock({
        site_codes: depotCodes,
        ...(search ? { search } : {}),
        limit: DIALOG_LIMIT,
      }),
    [api, depotCodes, search],
  );
  const levels = useResource(fetcher);
  const raw = useMemo(() => levels.data?.items ?? [], [levels.data]);
  const rows = useMemo(() => totalsOf(raw), [raw]);
  const qty = (value: number) => quantity(value.toFixed(3), i18n.language);

  const columns: Column<ArticleTotal>[] = [
    {
      key: "reference",
      header: t("stock.column.reference"),
      render: (row) => <span className="hardauto-ref">{row.reference}</span>,
    },
    {
      key: "designation",
      header: t("stock.column.designation"),
      render: (row) => row.designation,
    },
    {
      key: "depots",
      header: t("stockByDepot.column.depots"),
      align: "end",
      render: (row) => <span className="hardauto-num">{row.depots}</span>,
    },
    {
      key: "available",
      header: t("stock.column.available"),
      align: "end",
      render: (row) => (
        <span className="hardauto-num hardauto-num--strong">{qty(row.available)}</span>
      ),
    },
    {
      key: "reserved",
      header: t("stock.column.reserved"),
      align: "end",
      render: (row) => <span className="hardauto-num">{qty(row.reserved)}</span>,
    },
    {
      key: "real",
      header: t("stock.column.real"),
      align: "end",
      render: (row) => <span className="hardauto-num">{qty(row.real)}</span>,
    },
  ];

  return (
    <Dialog
      open
      onClose={onClose}
      title={name}
      description={[node.place, node.companies.join(", ")].filter(Boolean).join(" · ")}
      closeLabel={t("stockByDepot.close")}
    >
      <div className="hardauto-stock-depot-dialog">
        <dl className="hardauto-stock-depot-dialog__summary">
          <div>
            <dt>{t("stockByDepot.column.depots")}</dt>
            <dd>{node.depots}</dd>
          </div>
          <div>
            <dt>{t("stock.column.available")}</dt>
            <dd>{qty(node.available)}</dd>
          </div>
          <div>
            <dt>{t("stock.column.reserved")}</dt>
            <dd>{qty(node.reserved)}</dd>
          </div>
          <div>
            <dt>{t("stock.column.real")}</dt>
            <dd>{qty(node.real)}</dd>
          </div>
        </dl>
        <Input
          type="search"
          aria-label={t("stock.searchLabel")}
          placeholder={t("stock.searchPlaceholder")}
          value={term}
          onChange={(event) => setTerm(event.target.value)}
        />
        {levels.failed ? (
          <ErrorState
            title={t("error.title")}
            onRetry={levels.reload}
            retryLabel={t("error.retry")}
          />
        ) : levels.data === null && levels.loading ? (
          <LoadingState label={t("stock.loading")} />
        ) : (
          <>
            <DataTable<ArticleTotal>
              caption={t("stockByDepot.dialogCaption", { depot: name })}
              density="compact"
              columns={columns}
              rows={rows}
              rowKey={(row) => row.reference}
              loadingLabel={t("stock.loading")}
              empty={{ title: t("stockByDepot.noStock") }}
            />
            {raw.length >= DIALOG_LIMIT ? (
              <p className="hardauto-stock-depot-dialog__note">
                {t("stockByDepot.truncated", { count: DIALOG_LIMIT })}
              </p>
            ) : null}
          </>
        )}
      </div>
    </Dialog>
  );
}
